import { useEffect } from 'react';
import ToastModule from '@modules/ToastModule';
import { UNTIL_CLOSED_TOAST_DURATION } from './constants/untilClosedToastDuration';

type HammerToastDetail = {
  id?: string;
  eventType: 'add' | 'update' | 'dismiss';
  status?: 'success' | 'warning' | 'danger' | 'info';
  title?: string;
  message?: string;
  duration?: number | false;
  actions?: { primary?: HammerToastAction; secondary?: HammerToastAction };
};

// Typed loosely because the detail comes from whichever MFE fired the event.
// anvil2 also sends `appearance`, which the Anvil1 toast has no slot for.
type HammerToastAction = { label?: unknown; onClick?: unknown };
type ToastAction = {
  label: string;
  onClick: (e: React.SyntheticEvent<HTMLElement>) => void;
};

function isToastAction(
  action: HammerToastAction | undefined,
): action is ToastAction {
  return (
    typeof action?.label === 'string' && typeof action.onClick === 'function'
  );
}

function handleHammerToast(e: Event) {
  const detail = (e as CustomEvent<HammerToastDetail>).detail;
  if (detail?.eventType !== 'add') return;
  if (!detail.title) return;

  const status = detail.status === 'danger' ? 'critical' : detail.status;
  const primary = detail.actions?.primary;
  const secondary = detail.actions?.secondary;
  const hasAction = isToastAction(primary) || isToastAction(secondary);
  // anvil2 uses `duration: false` for a toast that stays until closed, and
  // keeps every toast with actions until closed whatever its duration.
  const eventDuration = detail.duration;
  const untilClosed = hasAction || eventDuration === false;
  const duration = untilClosed ? UNTIL_CLOSED_TOAST_DURATION : eventDuration;

  // Forward the event's own id so that if a flow ever leaves the MFE's
  // shadow toaster enabled (no host anvil2 Dialog mounted), the host and
  // MFE toasts dedup on this id instead of double-firing. `bridged` makes a
  // host close (X or timer) dismiss the anvil2 copies too (see hideToast).
  ToastModule.showToast({
    id: detail.id,
    bridged: true,
    ...(untilClosed && { untilClosed }),
    status,
    title: detail.title,
    toastBody: detail.message,
    duration,
    ...(isToastAction(primary) && {
      primaryActionName: primary.label,
      onPrimaryActionClick: primary.onClick,
    }),
    ...(isToastAction(secondary) && {
      secondaryActionName: secondary.label,
      onSecondaryActionClick: secondary.onClick,
    }),
  });
}

// Multiple MFE adapters can be mounted at once (e.g. InboxPage keeps every
// previously selected task's CustomerMfeAdapter alive, CSS-hidden, to
// preserve state), each calling this hook. window listeners are global, so
// registering one per mounted instance would fan a single hammer-toast event
// out to one ToastModule.showToast() call per instance. Ref-count instead so
// exactly one listener is ever attached.
let listenerRefCount = 0;

/**
 * Bridges anvil2 `hammer-toast` window events to CCP's native ToastModule.
 *
 * anvil2's `toast.success()` / `toast.danger()` dispatch CustomEvents on
 * `window` with type `"hammer-toast"` and `composed: true`, so they cross the
 * shadow boundary of an embedded MFE and reach the host `window`. CCP doesn't
 * use anvil2's `<Toaster>`, so this hook catches those events and forwards them
 * to ToastModule.showToast(). A toast raised while no bridged view is mounted
 * is not shown. ToastModule itself handles `toast.dismiss(id)`, and the
 * id-less `toast.dismiss()` for bridged toasts that are not sticky, for the
 * app's lifetime (see its `preload()`).
 *
 * Toast `actions` become the Anvil1 toast's primary/secondary buttons, calling
 * the MFE's own `onClick`. Neither toast closes on an action click; the MFE
 * closes it with `toast.dismiss(id)`, as it would under anvil2's `<Toaster>`.
 * A toast with actions stays until the MFE dismisses it, the CSR closes it,
 * or 8h pass, even after its MFE unmounts. So a Retry that fails again after
 * the CSR left raises a toast CCPro does not show, though the toaster of every
 * always-mounted anvil2 Dialog still collects it and shows it the next time
 * that dialog opens: Audio Settings on every page, and on Inbox the leave
 * guard and close-booking prompt.
 *
 * Needed for shadow-DOM MFEs (customer-mfe, VA) because anvil2's own
 * `<Toaster>` silences itself when it isn't the first `[data-anv='toaster']` in
 * the light DOM — a host `dialog-toaster` always wins that check, so the MFE's
 * shadow toaster never renders (CCPE-1317).
 */
export default function useAnvil2ToastBridge(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    if (listenerRefCount === 0) {
      window.addEventListener('hammer-toast', handleHammerToast);
    }
    listenerRefCount++;

    return () => {
      listenerRefCount--;
      if (listenerRefCount === 0) {
        window.removeEventListener('hammer-toast', handleHammerToast);
      }
    };
  }, [enabled]);
}
